import React, { useState } from 'react';
import {
  Card, Alert, Spinner, Badge,
  Container, Row, Col, Button,
  Modal
} from 'react-bootstrap';
import {
  FaBell, FaCalendarAlt, FaDownload, FaFilePdf,
  FaExclamationTriangle, FaWrench, FaInfoCircle, FaUsers,
  FaEye, FaPrint
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import './UserNotices.css';

// ═══════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE PRIORIDADES
// ═══════════════════════════════════════════════════════════════
const PRIORITY_CONFIG = {
  emergency: {
    label: 'Emergencia',
    icon: FaExclamationTriangle,
    colorClass: 'priority-emergency',
    badgeVariant: 'danger'
  },
  maintenance: {
    label: 'Mantenimiento',
    icon: FaWrench,
    colorClass: 'priority-maintenance',
    badgeVariant: 'warning'
  },
  informative: {
    label: 'Informativo',
    icon: FaInfoCircle,
    colorClass: 'priority-informative',
    badgeVariant: 'info'
  },
  meeting: {
    label: 'Reunión',
    icon: FaUsers,
    colorClass: 'priority-meeting',
    badgeVariant: 'primary'
  }
};

const UserNotices = ({ notificaciones = [], loading, error, user }) => {
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [imageUrl, setImageUrl] = useState(null);

  // ═══════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════
  const getToken = () => localStorage.getItem('token') || '';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-EC', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return 'Fecha inválida';
    }
  };

  const getPriorityConfig = (notice) => {
    // Primero intentar usar el campo priority de la BD
    if (notice.priority && PRIORITY_CONFIG[notice.priority]) {
      return PRIORITY_CONFIG[notice.priority];
    }

    // Fallback: detectar por título (compatibilidad con datos antiguos)
    const title = (notice.titulo || notice.title || '').toLowerCase();
    if (title.includes('emergencia') || title.includes('simulacro') || title.includes('urgente')) {
      return PRIORITY_CONFIG.emergency;
    } else if (title.includes('manten')) {
      return PRIORITY_CONFIG.maintenance;
    } else if (title.includes('reuni') || title.includes('asamblea')) {
      return PRIORITY_CONFIG.meeting;
    }
    return PRIORITY_CONFIG.informative;
  };

  const isImageFile = (notice) => {
    // Verificar por MIME type (attachment_type es el campo del backend)
    const mimeType = notice.attachment_type || notice.attachment_mime || '';
    if (mimeType.startsWith('image/')) {
      return true;
    }
    // Fallback: verificar por extensión del archivo
    const name = (notice.attachment_name || notice.attachment_path || '').toLowerCase();
    return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.gif') || name.endsWith('.webp');
  };

  // Filtrar avisos por tipo
  const getFilteredNotices = () => {
    if (activeFilter === 'all') return notificaciones;
    return notificaciones.filter(n => {
      const config = getPriorityConfig(n);
      const priorityKey = Object.keys(PRIORITY_CONFIG).find(key => PRIORITY_CONFIG[key] === config);
      return priorityKey === activeFilter;
    });
  };

  const filteredNotices = getFilteredNotices();

  // ═══════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleOpenModal = async (notice) => {
    setSelectedNotice(notice);

    // Si tiene imagen adjunta, cargarla
    if (notice.attachment_path && isImageFile(notice)) {
      try {
        const response = await fetch(`${API_ENDPOINTS.noticesById(notice.id)}/download`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          setImageUrl(url);
        }
      } catch (err) {
        console.error('Error loading image:', err);
      }
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (imageUrl) {
      window.URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DESCARGAR PDF
  // ═══════════════════════════════════════════════════════════════
  const handleDownloadPdf = async (notice, e) => {
    if (e) e.stopPropagation();
    setDownloadError(null);

    try {
      const noticeId = notice.id;
      const response = await fetch(`${API_ENDPOINTS.noticesById(noticeId)}/download`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = notice.attachment_name || 'documento.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading:', err);
      setDownloadError('No se pudo descargar el archivo');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // VER PDF EN MODAL
  // ═══════════════════════════════════════════════════════════════
  const handleViewPdf = async (notice, e) => {
    if (e) e.stopPropagation();
    setDownloadError(null);

    try {
      const noticeId = notice.id;
      const response = await fetch(`${API_ENDPOINTS.noticesById(noticeId)}/download`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar el archivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedNotice(notice);
      setShowPdfModal(true);
    } catch (err) {
      console.error('Error viewing:', err);
      setDownloadError('No se pudo cargar el archivo');
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // IMPRIMIR PDF
  // ═══════════════════════════════════════════════════════════════
  const handlePrintPdf = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // CONFIGURACIÓN DE FILTROS
  // ═══════════════════════════════════════════════════════════════
  const FILTER_OPTIONS = [
    { key: 'all', label: 'Todos', icon: FaBell },
    { key: 'informative', label: 'Informativo', icon: FaInfoCircle, color: 'info' },
    { key: 'maintenance', label: 'Mantenimiento', icon: FaWrench, color: 'warning' },
    { key: 'emergency', label: 'Emergencia', icon: FaExclamationTriangle, color: 'danger' },
    { key: 'meeting', label: 'Reunión', icon: FaUsers, color: 'primary' }
  ];

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="user-notices-wrapper">
      {/* Filtros por tipo — Tabs uniformes */}
      <div className="urbanq-user-tabs notices-tabs-wrapper">
        {FILTER_OPTIONS.map(filter => {
          const Icon = filter.icon;
          const count = filter.key === 'all'
            ? notificaciones.length
            : notificaciones.filter(n => {
                const config = getPriorityConfig(n);
                const priorityKey = Object.keys(PRIORITY_CONFIG).find(key => PRIORITY_CONFIG[key] === config);
                return priorityKey === filter.key;
              }).length;

          return (
            <button
              key={filter.key}
              className={`urbanq-user-tab ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              <Icon /> {filter.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Contenedor principal de avisos */}
      <Container fluid className="p-0 notices-container">
        {error && <Alert variant="danger">{error}</Alert>}
        {downloadError && (
          <Alert variant="warning" onClose={() => setDownloadError(null)} dismissible>
            {downloadError}
          </Alert>
        )}

        {loading && notificaciones.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Cargando notificaciones...</p>
          </div>
        ) : filteredNotices.length === 0 ? (
          <div className="empty-notices">
            <div className="empty-icon-container">
              <FaBell className="empty-icon" />
            </div>
            <h3>No hay avisos disponibles</h3>
            <p>{activeFilter === 'all' ? 'Por el momento no hay avisos o notificaciones para mostrar.' : `No hay avisos de tipo "${FILTER_OPTIONS.find(f => f.key === activeFilter)?.label || activeFilter}".`}</p>
          </div>
        ) : (
          <Row className="notices-grid">
            {filteredNotices.map((notice, index) => {
              const config = getPriorityConfig(notice);
              const IconComponent = config.icon;
              const titulo = notice.titulo || notice.title;
              const descripcion = notice.descripcion || notice.description;
              const fecha = notice.fecha || formatDate(notice.created_at);
              const hasAttachment = notice.attachment_path;

              return (
                <Col lg={4} md={6} key={notice.id} className="mb-4">
                  <Card
                    className={`notice-card h-100 ${config.colorClass}`}
                    onClick={() => handleOpenModal(notice)}
                  >
                    {/* Badge de prioridad */}
                    <div className={`priority-ribbon ${config.colorClass}`}>
                      <IconComponent className="ribbon-icon" />
                      <span>{config.label}</span>
                    </div>

                    <Card.Body className="d-flex flex-column">
                      <div className="notice-header-content">
                        <h5 className="notice-title">{titulo}</h5>
                        <Badge bg="light" text="dark" className="notice-date">
                          <FaCalendarAlt className="me-1" />
                          {fecha}
                        </Badge>
                      </div>

                      <Card.Text className="notice-description flex-grow-1">
                        {descripcion?.length > 120
                          ? `${descripcion.substring(0, 120)}...`
                          : descripcion}
                      </Card.Text>

                      {/* Indicador de archivo adjunto */}
                      {hasAttachment && (
                        <div className="attachment-indicator">
                          <FaFilePdf className="pdf-icon" />
                          <span>Documento adjunto</span>
                        </div>
                      )}
                    </Card.Body>

                    <Card.Footer className="notice-footer">
                      <div className="footer-actions">
                        {hasAttachment && (
                          <>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={(e) => handleViewPdf(notice, e)}
                              title="Ver documento"
                            >
                              <FaEye className="me-1" /> Ver
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={(e) => handleDownloadPdf(notice, e)}
                              title="Descargar documento"
                            >
                              <FaDownload className="me-1" /> Descargar
                            </Button>
                          </>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleOpenModal(notice); }}
                          className="btn-ver-completo"
                        >
                          Ver completo
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>

      {/* Modal para ver la notificación completa */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        className="notice-detail-modal"
      >
        {selectedNotice && (
          <>
            <Modal.Header closeButton className={`notice-modal-header ${getPriorityConfig(selectedNotice).colorClass}`}>
              <Modal.Title>
                {(() => {
                  const config = getPriorityConfig(selectedNotice);
                  const IconComponent = config.icon;
                  return <IconComponent className="me-2" />;
                })()}
                {selectedNotice.titulo || selectedNotice.title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <Badge
                  bg={getPriorityConfig(selectedNotice).badgeVariant}
                  className="priority-badge-large"
                >
                  {getPriorityConfig(selectedNotice).label}
                </Badge>
                <Badge bg="light" text="dark" className="notice-modal-date">
                  <FaCalendarAlt className="me-1" />
                  Publicado: {selectedNotice.fecha || formatDate(selectedNotice.created_at)}
                </Badge>
              </div>

              <div className="notice-modal-description">
                {selectedNotice.descripcion || selectedNotice.description}
              </div>

              {/* Archivo adjunto */}
              {selectedNotice.attachment_path && (
                <div className="attachment-section mt-4">
                  {isImageFile(selectedNotice) ? (
                    <>
                      <h6 className="attachment-title attachment-title-image">
                        <FaEye className="me-2 text-success" />
                        Imagen adjunta
                      </h6>
                      {imageUrl && (
                        <div className="attachment-image-container">
                          <img
                            src={imageUrl}
                            alt="Imagen adjunta"
                            className="attachment-image"
                          />
                        </div>
                      )}
                      <div className="attachment-actions mt-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={(e) => handleDownloadPdf(selectedNotice, e)}
                        >
                          <FaDownload className="me-1" /> Descargar imagen
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h6 className="attachment-title">
                        <FaFilePdf className="me-2 text-danger" />
                        Documento adjunto
                      </h6>
                      <div className="attachment-actions">
                        <span className="attachment-name">
                          {selectedNotice.attachment_name || 'documento.pdf'}
                        </span>
                        <div className="attachment-buttons">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={(e) => handleViewPdf(selectedNotice, e)}
                          >
                            <FaEye className="me-1" /> Ver
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={(e) => handleDownloadPdf(selectedNotice, e)}
                          >
                            <FaDownload className="me-1" /> Descargar
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cerrar
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Modal para visualizar PDF */}
      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        centered
        size="xl"
        className="pdf-viewer-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFilePdf className="me-2 text-danger" />
            {selectedNotice?.attachment_name || 'Documento'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Visor de PDF"
              className="pdf-viewer-iframe"
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handlePrintPdf}>
            <FaPrint className="me-1" /> Imprimir
          </Button>
          <Button
            variant="success"
            onClick={(e) => selectedNotice && handleDownloadPdf(selectedNotice, e)}
          >
            <FaDownload className="me-1" /> Descargar
          </Button>
          <Button variant="secondary" onClick={handleClosePdfModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserNotices;
